import { NgForOf, NgSwitch, NgSwitchCase } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import { REPORT_CHART_TYPE_OPTIONS } from '../../../../services/report-chart-types';

interface ToolbarOption {
	render: ReportBlockRender;
	label: string;
	tooltip: string;
	current: boolean;
	readsPoorly: boolean;
}

/**
 * The chart-type menu: one icon per type a chart can be switched to.
 *
 * EDITOR ONLY, and it renders nothing about visibility itself: whoever places
 * it decides when it shows (on hover or keyboard focus of the chart) through
 * `--report-chart-types-opacity` and `--report-chart-types-events`, because
 * only the chart's own box knows what "hovering the chart" means for it.
 *
 * Every type offered can be picked, including one that reads the numbers
 * badly — the reader asked for it. Such a type carries a small mark and says
 * why in its tooltip; it is never disabled.
 */
@Component({
	standalone: true,
	selector: 'app-report-chart-type-toolbar',
	imports: [NgForOf, NgSwitch, NgSwitchCase, MatTooltipModule],
	template: `
		<div class="chart-types" role="toolbar" aria-label="Chart type">
			<button
				*ngFor="let option of options; trackBy: trackByRender"
				type="button"
				class="chart-types__button"
				[class.chart-types__button--current]="option.current"
				[class.chart-types__button--poor]="option.readsPoorly"
				[attr.aria-pressed]="option.current"
				[attr.aria-label]="option.tooltip"
				[matTooltip]="option.tooltip"
				matTooltipPosition="above"
				matTooltipClass="sentiment-tooltip"
				(click)="pick(option)"
			>
				<svg
					viewBox="0 0 16 16"
					width="16"
					height="16"
					aria-hidden="true"
					[ngSwitch]="option.render"
				>
					<g *ngSwitchCase="'line'">
						<polyline
							class="stroke"
							points="2,12 6,7.5 9,9.5 14,3.5"
						/>
					</g>
					<g *ngSwitchCase="'area'">
						<path
							class="fill-soft"
							d="M2 14V10l4-4.5 3 2 5-4.5V14z"
						/>
						<polyline
							class="stroke"
							points="2,10 6,5.5 9,7.5 14,3"
						/>
					</g>
					<g *ngSwitchCase="'bar'">
						<rect
							class="fill"
							x="2"
							y="8"
							width="3"
							height="6"
							rx="0.6"
						/>
						<rect
							class="fill"
							x="6.5"
							y="3"
							width="3"
							height="11"
							rx="0.6"
						/>
						<rect
							class="fill"
							x="11"
							y="6"
							width="3"
							height="8"
							rx="0.6"
						/>
					</g>
					<g *ngSwitchCase="'stacked_bar'">
						<rect
							class="fill"
							x="2"
							y="10"
							width="3"
							height="4"
							rx="0.6"
						/>
						<rect
							class="fill-soft"
							x="2"
							y="6"
							width="3"
							height="4"
							rx="0.6"
						/>
						<rect
							class="fill"
							x="6.5"
							y="8"
							width="3"
							height="6"
							rx="0.6"
						/>
						<rect
							class="fill-soft"
							x="6.5"
							y="2.5"
							width="3"
							height="5.5"
							rx="0.6"
						/>
						<rect
							class="fill"
							x="11"
							y="11"
							width="3"
							height="3"
							rx="0.6"
						/>
						<rect
							class="fill-soft"
							x="11"
							y="7.5"
							width="3"
							height="3.5"
							rx="0.6"
						/>
					</g>
					<g *ngSwitchCase="'ranked_bar'">
						<rect
							class="fill"
							x="2"
							y="2.5"
							width="12"
							height="2.6"
							rx="0.6"
						/>
						<rect
							class="fill"
							x="2"
							y="6.7"
							width="8.5"
							height="2.6"
							rx="0.6"
						/>
						<rect
							class="fill"
							x="2"
							y="10.9"
							width="5"
							height="2.6"
							rx="0.6"
						/>
					</g>
					<g *ngSwitchCase="'doughnut'">
						<circle class="ring-soft" cx="8" cy="8" r="5" />
						<path class="ring" d="M8 3a5 5 0 0 1 4.33 7.5" />
					</g>
					<g *ngSwitchCase="'pie'">
						<circle class="fill-soft" cx="8" cy="8" r="6" />
						<path class="fill" d="M8 8V2a6 6 0 0 1 5.2 9z" />
					</g>
					<g *ngSwitchCase="'polarArea'">
						<path
							class="fill"
							d="M8 8V1.5a6.5 6.5 0 0 1 6.5 6.5z"
						/>
						<path
							class="fill-soft"
							d="M8 8h4.5A4.5 4.5 0 0 1 8 12.5z"
						/>
						<path class="fill" d="M8 8v3A3 3 0 0 1 5 8z" />
						<path
							class="fill-soft"
							d="M8 8H2.5A5.5 5.5 0 0 1 8 2.5z"
						/>
					</g>
					<g *ngSwitchCase="'table'">
						<rect
							class="stroke"
							x="2"
							y="3"
							width="12"
							height="10"
							rx="1.2"
						/>
						<line class="stroke" x1="2" y1="6.5" x2="14" y2="6.5" />
						<line
							class="stroke"
							x1="2"
							y1="9.75"
							x2="14"
							y2="9.75"
						/>
						<line
							class="stroke"
							x1="6.5"
							y1="6.5"
							x2="6.5"
							y2="13"
						/>
					</g>
					<g *ngSwitchCase="'kpi'">
						<rect
							class="stroke"
							x="1.5"
							y="4"
							width="5.5"
							height="8"
							rx="1.2"
						/>
						<rect
							class="stroke"
							x="9"
							y="4"
							width="5.5"
							height="8"
							rx="1.2"
						/>
						<line
							class="stroke"
							x1="3"
							y1="9.5"
							x2="5.5"
							y2="9.5"
						/>
						<line
							class="stroke"
							x1="10.5"
							y1="9.5"
							x2="13"
							y2="9.5"
						/>
					</g>
					<g *ngSwitchCase="'matrix'">
						<rect
							class="fill"
							x="2"
							y="2"
							width="3.5"
							height="3.5"
							rx="0.6"
						/>
						<rect
							class="fill-soft"
							x="6.25"
							y="2"
							width="3.5"
							height="3.5"
							rx="0.6"
						/>
						<rect
							class="fill"
							x="10.5"
							y="2"
							width="3.5"
							height="3.5"
							rx="0.6"
						/>
						<rect
							class="fill-soft"
							x="2"
							y="6.25"
							width="3.5"
							height="3.5"
							rx="0.6"
						/>
						<rect
							class="fill"
							x="6.25"
							y="6.25"
							width="3.5"
							height="3.5"
							rx="0.6"
						/>
						<rect
							class="fill-soft"
							x="10.5"
							y="6.25"
							width="3.5"
							height="3.5"
							rx="0.6"
						/>
						<rect
							class="fill-soft"
							x="2"
							y="10.5"
							width="3.5"
							height="3.5"
							rx="0.6"
						/>
						<rect
							class="fill"
							x="6.25"
							y="10.5"
							width="3.5"
							height="3.5"
							rx="0.6"
						/>
						<rect
							class="fill-soft"
							x="10.5"
							y="10.5"
							width="3.5"
							height="3.5"
							rx="0.6"
						/>
					</g>
					<g *ngSwitchCase="'wordCloud'">
						<rect
							class="fill"
							x="2"
							y="3"
							width="8"
							height="3"
							rx="1.5"
						/>
						<rect
							class="fill-soft"
							x="11"
							y="3.5"
							width="3"
							height="2"
							rx="1"
						/>
						<rect
							class="fill-soft"
							x="2"
							y="7.5"
							width="4"
							height="2"
							rx="1"
						/>
						<rect
							class="fill"
							x="7"
							y="7"
							width="7"
							height="3"
							rx="1.5"
						/>
						<rect
							class="fill-soft"
							x="4"
							y="11.5"
							width="6"
							height="2"
							rx="1"
						/>
					</g>
					<g *ngSwitchCase="'timeline'">
						<line class="stroke" x1="4" y1="2.5" x2="4" y2="13.5" />
						<circle class="fill" cx="4" cy="4" r="1.6" />
						<circle class="fill" cx="4" cy="8" r="1.6" />
						<circle class="fill" cx="4" cy="12" r="1.6" />
						<line class="stroke" x1="7.5" y1="4" x2="14" y2="4" />
						<line class="stroke" x1="7.5" y1="8" x2="12" y2="8" />
						<line class="stroke" x1="7.5" y1="12" x2="13" y2="12" />
					</g>
					<g *ngSwitchCase="'rating_bars'">
						<polygon
							class="fill"
							points="3.5,5 4.2,7 6.35,7.07 4.64,8.37 5.26,10.43 3.5,9.2 1.74,10.43 2.36,8.37 0.65,7.07 2.8,7"
						/>
						<rect
							class="fill"
							x="8"
							y="3"
							width="6.5"
							height="2.2"
							rx="0.6"
						/>
						<rect
							class="fill-soft"
							x="8"
							y="6.9"
							width="4.5"
							height="2.2"
							rx="0.6"
						/>
						<rect
							class="fill-soft"
							x="8"
							y="10.8"
							width="2.5"
							height="2.2"
							rx="0.6"
						/>
					</g>
				</svg>
			</button>
		</div>
	`,
	styles: [
		`
			:host {
				display: block;
			}

			.chart-types {
				display: inline-flex;
				align-items: center;
				gap: 2px;
				padding: 3px;
				border: 1px solid #d6e7ff;
				border-radius: 10px;
				background: #ffffff;
				box-shadow: 0 4px 14px rgba(35, 78, 143, 0.12);
				opacity: var(--report-chart-types-opacity, 0);
				pointer-events: var(--report-chart-types-events, none);
				transition: opacity 0.15s ease;
			}

			/* A screen with no hover would otherwise never show it. */
			@media (hover: none) {
				.chart-types {
					opacity: 1;
					pointer-events: auto;
				}
			}

			.chart-types__button {
				position: relative;
				display: inline-flex;
				align-items: center;
				justify-content: center;
				width: 28px;
				height: 28px;
				padding: 0;
				border: none;
				border-radius: 7px;
				background: transparent;
				color: #6b7796;
				cursor: pointer;
			}

			.chart-types__button:hover,
			.chart-types__button:focus-visible {
				background: #f2f6fd;
				color: #2872e1;
				outline: none;
			}

			.chart-types__button--current {
				background: #e8f0fd;
				color: #2872e1;
			}

			/* A type that reads these numbers badly: still a type, marked. */
			.chart-types__button--poor::after {
				content: '';
				position: absolute;
				top: 4px;
				right: 4px;
				width: 5px;
				height: 5px;
				border-radius: 50%;
				background: #f5a623;
			}

			svg {
				display: block;
			}

			.stroke {
				fill: none;
				stroke: currentColor;
				stroke-width: 1.5;
				stroke-linecap: round;
				stroke-linejoin: round;
			}

			.fill {
				fill: currentColor;
			}

			.fill-soft {
				fill: currentColor;
				opacity: 0.35;
			}

			.ring {
				fill: none;
				stroke: currentColor;
				stroke-width: 3;
			}

			.ring-soft {
				fill: none;
				stroke: currentColor;
				stroke-width: 3;
				opacity: 0.35;
			}
		`
	]
})
export class ReportChartTypeToolbarComponent implements OnChanges {
	/** Every type the chart may be switched to. */
	@Input() types: readonly ReportBlockRender[] = [];
	/** The type it is drawn as now. */
	@Input() current: ReportBlockRender | null = null;
	/** Why a type reads these numbers badly, per type; absent where it is fine. */
	@Input() advice: Partial<Record<ReportBlockRender, string>> = {};
	@Output() picked = new EventEmitter<ReportBlockRender>();

	/** Built on change rather than in the template, which reads it per pass. */
	options: ToolbarOption[] = [];

	ngOnChanges(): void {
		const offered = new Set(this.types);
		this.options = REPORT_CHART_TYPE_OPTIONS.filter(option =>
			offered.has(option.render)
		).map(option => {
			const reason = this.advice?.[option.render];
			const current = option.render === this.current;
			return {
				render: option.render,
				label: option.label,
				tooltip: reason
					? `${option.label} — reads poorly: ${reason}`
					: current
					? `${option.label} (current)`
					: option.label,
				current,
				readsPoorly: Boolean(reason)
			};
		});
	}

	pick(option: ToolbarOption): void {
		if (!option.current) {
			this.picked.emit(option.render);
		}
	}

	trackByRender(_index: number, option: ToolbarOption): string {
		return option.render;
	}
}
